import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router';

import type { BatchStatus } from '@cartwhl/common/api/getBatch';

import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import CopyIcon from '@cartwhl/ui/icons/CopyIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';

import { useStore, withProvider } from '../batchComicProcessStore';
import type { FileMetadata } from '../types';

import BatchComicJobErrorModal from '../../shared/BatchComicJobErrorModal';
import BatchMotionThumbnailLoadingChip from '../../shared/BatchMotionThumbnailLoadingChip';
import BatchMotionJobPreviewModal, {
  batchMotionFileCanPreview as fileCanPreview,
  formatExportSettingsSummary,
  type BatchPreviewNavigationJob,
} from '../../shared/BatchMotionJobPreviewModal';
import {
  blurFocusedElement,
  useComicSourceVideoOverlay,
  useThumbnailDownloadGate,
} from '../../shared/batchMotionPreviewPolling';
import {
  BatchMotionJobThumbnailGrid,
  type BatchMotionThumbnailGridFile,
} from '../../shared/BatchMotionJobThumbnailGrid';

/** Matches preview modal nav controls: secondary + hairline border. */
const OUTLINE_NAV_BUTTON =
  '!bg-white dark:!bg-gray-90 !border-gray-30 dark:!border-gray-75 border hover:!bg-gray-15 dark:hover:!bg-gray-75';

function batchProcessStatusLabel(
  s: 'idle' | 'loading' | BatchStatus,
): string {
  if (s === 'idle' || s === 'loading') return 'Loading';
  const labels: Record<BatchStatus, string> = {
    VALIDATING: 'Validating',
    QUEUED: 'Queued',
    IN_PROGRESS: 'Processing',
    COMPLETED: 'Completed',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
    FAILED: 'Failed',
  };
  return labels[s] ?? s;
}

function ProcessingScreen() {
  const store = useStore();
  const navigate = useNavigate();
  const { id: processIdFromUrl } = useParams<{ id: string }>();
  const {
    fileMetadata,
    processName,
    processId,
    estimatedProcessingTime,
    status,
    downloadUrl,
    batchExportSettings,
  } = store.state;

  const [previewJobId, setPreviewJobId] = useState<string | null>(null);

  const openPreviewJob = useCallback((jobId: string) => {
    blurFocusedElement();
    setPreviewJobId(jobId);
  }, []);
  const previewFile = useMemo(
    () =>
      previewJobId
        ? fileMetadata.find((f) => f.id === previewJobId)
        : undefined,
    [previewJobId, fileMetadata],
  );

  /** Preview modal “n of m” and prev/next only include jobs that can open the viewer (completed + BVH). */
  const previewNavJobs = useMemo<BatchPreviewNavigationJob[]>(
    () =>
      fileMetadata
        .filter((f) => fileCanPreview(f))
        .map((f, i) => ({
          id: f.id,
          name: f.name,
          batchMotionNumber: i + 1,
          canPreview: true,
        })),
    [fileMetadata],
  );

  const previewableJobCount = previewNavJobs.length;

  const batchMotionNumberForPreview = useMemo(() => {
    if (!previewFile) return 1;
    const i = previewNavJobs.findIndex((j) => j.id === previewFile.id);
    return i >= 0 ? i + 1 : 1;
  }, [previewFile, previewNavJobs]);

  const getSourceVideoMedia = useCallback(
    (mediaId: string) => store.getSourceVideoMedia(mediaId),
    [store],
  );

  const comicSourceOverlay = useComicSourceVideoOverlay(
    getSourceVideoMedia,
    previewFile?.sourceVideoMediaID,
  );

  const pollJobZipExportReadiness = useCallback(() => {
    if (!previewJobId) {
      return Promise.resolve({
        downloadExportsReady: false,
        hasFailedExport: false,
        exportRequestCount: 0,
        expectedExportRequests: 0,
        completedExportRequests: 0,
      });
    }
    return store.pollJobZipExportReadiness(previewJobId);
  }, [previewJobId, store]);

  const pollJobExportStatus = useCallback(
    (jobId: string) => store.pollJobZipExportReadiness(jobId),
    [store],
  );

  const { canDownloadFile: canDownloadThumbnail } = useThumbnailDownloadGate(
    processId,
    fileMetadata,
    pollJobExportStatus,
  );

  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(
    () => new Set(),
  );
  const lastSelectedJobIdRef = useRef<string | null>(null);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);

  useEffect(() => {
    const valid = new Set(fileMetadata.map((f) => f.id));
    setSelectedJobIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) {
        if (valid.has(id)) next.add(id);
      }
      if (next.size === prev.size) {
        for (const id of prev) {
          if (!next.has(id)) {
            return next;
          }
        }
        return prev;
      }
      return next;
    });
  }, [fileMetadata]);

  const handleThumbnailCheckbox = useCallback(
    (orderedIds: string[]) =>
      (jobId: string, event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        const isShift = event.shiftKey;

        setSelectedJobIds((prev) => {
          const next = new Set(prev);
          if (isShift) {
            const anchor = lastSelectedJobIdRef.current;
            if (
              anchor &&
              orderedIds.includes(anchor) &&
              orderedIds.includes(jobId)
            ) {
              const a = orderedIds.indexOf(anchor);
              const b = orderedIds.indexOf(jobId);
              const [lo, hi] = a <= b ? [a, b] : [b, a];
              for (let i = lo; i <= hi; i++) {
                next.add(orderedIds[i]!);
              }
            } else {
              next.add(jobId);
            }
          } else if (next.has(jobId)) {
            next.delete(jobId);
          } else {
            next.add(jobId);
          }
          return next;
        });
        lastSelectedJobIdRef.current = jobId;
      },
    [],
  );

  const handleThumbnailDownload = useCallback(
    async (jobId: string, event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      const file = fileMetadata.find((f) => f.id === jobId);
      if (!file) return;
      setDownloadingJobId(jobId);
      try {
        await store.downloadJob(jobId, file.name);
      } finally {
        setDownloadingJobId(null);
      }
    },
    [fileMetadata, store],
  );

  const handleBulkDownloadSelected = useCallback(async () => {
    if (selectedJobIds.size === 0) return;
    setBulkDownloadLoading(true);
    try {
      for (const jobId of selectedJobIds) {
        const file = fileMetadata.find((f) => f.id === jobId);
        if (!file || file.status !== 'COMPLETED') continue;
        await store.downloadJob(jobId, file.name);
        await new Promise((r) => setTimeout(r, 350));
      }
    } finally {
      setBulkDownloadLoading(false);
    }
  }, [selectedJobIds, fileMetadata, store]);

  useEffect(() => {
    if (processIdFromUrl && processIdFromUrl !== processId) {
      store.loadProcess(processIdFromUrl);
    }
  }, [processIdFromUrl, processId, store]);

  useEffect(() => {
    return () => store.stopPolling();
  }, [store]);

  const includedFiles = useMemo(
    () =>
      fileMetadata.filter(
        (f) =>
          f.status === 'COMPLETED' ||
          f.status === 'QUEUED' ||
          f.status === 'GENERATING',
      ),
    [fileMetadata],
  );
  const omittedFiles = useMemo(
    () =>
      fileMetadata.filter(
        (f) => f.status === 'FAILED' || f.status === 'CANCELLED',
      ),
    [fileMetadata],
  );

  const hasJobFailures = omittedFiles.length > 0;
  const [comicFileFilter, setComicFileFilter] = useState<
    'completed' | 'errors'
  >('completed');
  const [errorModalJobId, setErrorModalJobId] = useState<string | null>(null);

  const isLoading = status === 'loading' || status === 'idle';
  const isComplete = status === 'COMPLETED';
  const isError = status === 'FAILED';
  const allFilesResolved =
    fileMetadata.length > 0 &&
    fileMetadata.every(
      (f) =>
        f.status === 'COMPLETED' ||
        f.status === 'FAILED' ||
        f.status === 'CANCELLED',
    );

  /** Every motion failed (or was cancelled); no completed / in-flight successes left. */
  const fullJobFailure = useMemo(
    () =>
      allFilesResolved &&
      fileMetadata.length > 0 &&
      includedFiles.length === 0 &&
      omittedFiles.length > 0,
    [
      allFilesResolved,
      fileMetadata.length,
      includedFiles.length,
      omittedFiles.length,
    ],
  );

  const errorsViewActive = useMemo(
    () => fullJobFailure || (hasJobFailures && comicFileFilter === 'errors'),
    [fullJobFailure, hasJobFailures, comicFileFilter],
  );

  useEffect(() => {
    if (fullJobFailure) {
      setComicFileFilter('errors');
    }
  }, [fullJobFailure]);

  useEffect(() => {
    if (comicFileFilter === 'errors') {
      setSelectedJobIds(new Set());
    } else {
      setErrorModalJobId(null);
    }
  }, [comicFileFilter]);

  const motionNumberInBatch = useCallback(
    (id: string) => fileMetadata.findIndex((f) => f.id === id) + 1 || 1,
    [fileMetadata],
  );

  const includedMotionNumbers = useMemo(
    () => includedFiles.map((f) => motionNumberInBatch(f.id)),
    [includedFiles, motionNumberInBatch],
  );

  const omittedMotionNumbers = useMemo(
    () => omittedFiles.map((f) => motionNumberInBatch(f.id)),
    [omittedFiles, motionNumberInBatch],
  );

  const errorNavJobs = useMemo(
    () =>
      omittedFiles.map((f, i) => ({
        id: f.id,
        name: f.name,
        batchMotionNumber: i + 1,
      })),
    [omittedFiles],
  );

  const errorModalFile = useMemo(
    () =>
      errorModalJobId
        ? omittedFiles.find((f) => f.id === errorModalJobId)
        : undefined,
    [errorModalJobId, omittedFiles],
  );

  const includedOrderIds = useMemo(
    () => includedFiles.map((f) => f.id),
    [includedFiles],
  );
  const allFilesOrderIds = useMemo(
    () => fileMetadata.map((f) => f.id),
    [fileMetadata],
  );

  const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 60) return 'Less than 1 min';
    const mins = Math.ceil(seconds / 60);
    return `~${mins} min`;
  };

  const handleBackToHome = () => {
    store.reset();
    navigate('/comic');
  };

  const headerTitle =
    processName?.trim() ||
    (processId ? `Batch ${processId}` : 'Batch');

  const motionCount = fileMetadata.length;
  const showHeaderProgressSpinner = fileMetadata.some(
    (f) => f.status === 'QUEUED' || f.status === 'GENERATING',
  );
  const downloadAllEnabled = Boolean(isComplete && downloadUrl);

  const batchDetailsBoxClass = useMemo(() => {
    if (errorsViewActive) {
      return 'bg-red/10 dark:bg-red/20';
    }
    if (isComplete) {
      return 'bg-green/10 dark:bg-green/20';
    }
    if (isError) {
      return 'bg-red/10 dark:bg-red/20';
    }
    return 'bg-blue/10 dark:bg-blue/20';
  }, [errorsViewActive, isComplete, isError]);

  const batchMotionNumberForErrorModal = useMemo(() => {
    if (!errorModalFile) return 1;
    const i = omittedFiles.findIndex((f) => f.id === errorModalFile.id);
    return i >= 0 ? i + 1 : 1;
  }, [errorModalFile, omittedFiles]);

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="container mx-auto py-12">
          <div className="flex min-h-96 flex-col items-center justify-center gap-4">
            <Loading className="text-green h-12 w-12" />
            <Text variant="studio/body/md" className="text-secondary">
              Loading process status...
            </Text>
          </div>
        </div>
      </div>
    );
  }

  if (isError && fileMetadata.length === 0) {
    return (
      <div className="min-h-screen">
        <div className="container mx-auto py-12">
          <div className="flex min-h-96 flex-col items-center justify-center gap-4">
            <WarningIcon className="text-red h-12 w-12" />
            <Text variant="studio/body/md" className="text-secondary">
              Failed to load process. The process ID may be invalid.
            </Text>
            <Button variant="secondary" onClick={handleBackToHome}>
              Back to Comic
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-secondary rounded-2xl p-4">
          <div className="bg-primary border-primary flex flex-col gap-4 rounded-lg border p-8">
            <div className="border-primary border-b pb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    className={`h-10 w-10 shrink-0 p-0 ${OUTLINE_NAV_BUTTON}`}
                    icon={<ChevronLeftIcon className="h-5 w-5" />}
                    aria-label="Back to Comic home"
                    onClick={handleBackToHome}
                  />
                  <div className="text-secondary flex min-w-0 items-center gap-2 sm:gap-3">
                    <Text
                      as="h1"
                      variant="studio/body/medium/24"
                      className="text-primary min-w-0 max-w-[min(100%,20rem)] truncate sm:max-w-112"
                    >
                      {headerTitle}
                    </Text>
                    {showHeaderProgressSpinner ? (
                      <BatchMotionThumbnailLoadingChip layout="inline" />
                    ) : null}
                    <Text
                      as="span"
                      variant="studio/body/medium/24"
                      className="text-secondary inline-flex shrink-0 items-baseline gap-1 whitespace-nowrap"
                    >
                      <span className="tabular-nums">{motionCount}</span>
                      <span>
                        {motionCount === 1 ? 'motion' : 'motions'}
                      </span>
                    </Text>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-3">
                  {!(errorsViewActive) &&
                  selectedJobIds.size > 0 ? (
                    <div className="bg-blue/10 dark:bg-blue/20 flex items-center gap-2 rounded-2xl px-3 py-2">
                      <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        icon={<CrossIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedJobIds(new Set());
                        }}
                        className="h-6 w-6 p-0"
                        aria-label="Clear selection"
                      />
                      <Text variant="studio/body/sm" className="text-primary">
                        {selectedJobIds.size} Selected
                      </Text>
                    </div>
                  ) : null}
                  {!(errorsViewActive) &&
                  selectedJobIds.size > 0 ? (
                    <Button
                      type="button"
                      variant="primary"
                      size="lg"
                      alignIcon="end"
                      loading={bulkDownloadLoading}
                      icon={<DownloadIcon className="h-5 w-5" />}
                      onClick={() => void handleBulkDownloadSelected()}
                    >
                      Download selected
                    </Button>
                  ) : downloadAllEnabled && downloadUrl ? (
                    <a
                      href={downloadUrl}
                      download
                      className="inline-flex shrink-0 no-underline"
                    >
                      <Button
                        variant="primary"
                        size="lg"
                        alignIcon="end"
                        icon={<DownloadIcon className="h-5 w-5" />}
                      >
                        Download all
                      </Button>
                    </a>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      size="lg"
                      alignIcon="end"
                      disabled
                      icon={<DownloadIcon className="h-5 w-5" />}
                    >
                      Download all
                    </Button>
                  )}
                </div>
              </div>
            </div>

          {hasJobFailures && !fullJobFailure ? (
            <div
              className="inline-flex w-fit max-w-full flex-wrap gap-2"
              role="tablist"
              aria-label="Show completed or failed files"
            >
              <Button
                type="button"
                role="tab"
                aria-selected={comicFileFilter === 'completed'}
                variant={
                  comicFileFilter === 'completed' ? 'secondary' : 'tertiary'
                }
                size="lg"
                icon={<CheckmarkIcon className="text-green" />}
                onClick={() => setComicFileFilter('completed')}
              >
                Completed files ({includedFiles.length})
              </Button>
              <Button
                type="button"
                role="tab"
                aria-selected={comicFileFilter === 'errors'}
                variant={comicFileFilter === 'errors' ? 'secondary' : 'tertiary'}
                size="lg"
                icon={<CrossIcon className="text-red" />}
                onClick={() => setComicFileFilter('errors')}
              >
                Errors ({omittedFiles.length})
              </Button>
            </div>
          ) : null}

          {/* Batch details */}
          <div className={`rounded-lg p-8 ${batchDetailsBoxClass}`}>
            <div className="grid grid-cols-2 items-start gap-x-4 gap-y-8 sm:gap-x-12 lg:gap-x-20">
              {errorsViewActive ? (
                <>
                  <div className="min-w-0 flex flex-col gap-2">
                    <Text variant="studio/body/md" className="text-secondary">
                      Status
                    </Text>
                    <Text
                      variant="studio/body/medium/md"
                      className="text-primary"
                    >
                      Error
                    </Text>
                  </div>
                  <div className="min-w-0 flex flex-col gap-2">
                    <Text variant="studio/body/md" className="text-secondary">
                      Error details
                    </Text>
                    <ul className="text-primary list-disc space-y-1 pl-5">
                      {omittedFiles.map((f) => (
                        <li key={f.id}>
                          <Text
                            as="span"
                            variant="studio/body/medium/md"
                            className="text-primary"
                          >
                            {f.errorMessage?.trim()
                              ? `${f.name}: ${f.errorMessage.trim()}`
                              : `${f.name}: No error message was provided.`}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="col-span-2 min-w-0 flex flex-col gap-2">
                    <Text variant="studio/body/md" className="text-secondary">
                      Comic batch ID
                    </Text>
                    <div className="flex min-w-0 w-full max-w-full items-start gap-2 overflow-hidden">
                      <Button
                        variant="tertiary"
                        icon={<CopyIcon className="h-6 w-6" />}
                        alignIcon="end"
                        onClick={() => store.copyProcessIdToClipboard()}
                        className="text-blue hover:text-blue-60 relative bottom-2 mb-[-20px] ml-0 min-w-0 max-w-full transition-colors sm:ml-[-16px]"
                        title="Copy to clipboard"
                      >
                        <Text
                          variant="studio/body/medium/md"
                          className="text-primary min-w-0 truncate"
                        >
                          {processId}
                        </Text>
                      </Button>
                    </div>
                  </div>
                  {!isComplete && !isError ? (
                    <div className="col-span-2 min-w-0 flex flex-col gap-2">
                      <Text variant="studio/body/md" className="text-secondary">
                        Estimated time of completion
                      </Text>
                      <Text
                        variant="studio/body/medium/md"
                        className="text-primary"
                      >
                        {formatTime(estimatedProcessingTime)}
                      </Text>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="min-w-0 flex flex-col gap-2">
                    <Text variant="studio/body/md" className="text-secondary">
                      Status
                    </Text>
                    <Text
                      variant="studio/body/medium/md"
                      className="text-primary"
                    >
                      {batchProcessStatusLabel(status)}
                    </Text>
                  </div>
                  <div className="min-w-0 flex flex-col gap-2">
                    <Text variant="studio/body/md" className="text-secondary">
                      Job details
                    </Text>
                    <Text
                      variant="studio/body/medium/md"
                      className="text-primary"
                    >
                      {formatExportSettingsSummary(batchExportSettings)}
                    </Text>
                  </div>
                  <div className="col-span-2 min-w-0 flex flex-col gap-2">
                    <Text variant="studio/body/md" className="text-secondary">
                      Comic batch ID
                    </Text>
                    <div className="flex min-w-0 w-full max-w-full items-start gap-2 overflow-hidden">
                      <Button
                        variant="tertiary"
                        icon={<CopyIcon className="h-6 w-6" />}
                        alignIcon="end"
                        onClick={() => store.copyProcessIdToClipboard()}
                        className="text-blue hover:text-blue-60 relative bottom-2 mb-[-20px] ml-0 min-w-0 max-w-full transition-colors sm:ml-[-16px]"
                        title="Copy to clipboard"
                      >
                        <Text
                          variant="studio/body/medium/md"
                          className="text-primary min-w-0 truncate"
                        >
                          {processId}
                        </Text>
                      </Button>
                    </div>
                  </div>
                  {!isComplete && !isError ? (
                    <div className="min-w-0 flex flex-col gap-2">
                      <Text variant="studio/body/md" className="text-secondary">
                        Estimated time of completion
                      </Text>
                      <Text
                        variant="studio/body/medium/md"
                        className="text-primary"
                      >
                        {formatTime(estimatedProcessingTime)}
                      </Text>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>

          {/* File Lists */}
          {hasJobFailures ? (
            <div className="flex flex-col gap-2">
              {errorsViewActive ? (
                <BatchMotionJobThumbnailGrid
                  files={omittedFiles}
                  motionNumbers={omittedMotionNumbers}
                  onPreviewJob={() => {}}
                  allowErrorExpand={false}
                  selectionEnabled={false}
                  errorThumbnailVariant="comic-errors"
                  onErrorThumbnailClick={(id) => setErrorModalJobId(id)}
                />
              ) : (
                <BatchMotionJobThumbnailGrid
                  files={includedFiles}
                  motionNumbers={includedMotionNumbers}
                  onPreviewJob={openPreviewJob}
                  selectionEnabled
                  selectedJobIds={selectedJobIds}
                  onCheckboxClick={handleThumbnailCheckbox(includedOrderIds)}
                  onDownloadJob={(jobId, e) =>
                    void handleThumbnailDownload(jobId, e)
                  }
                  canDownloadFile={canDownloadThumbnail}
                  downloadingJobId={downloadingJobId}
                />
              )}
            </div>
          ) : allFilesResolved ? (
            <div className="flex flex-col gap-6 lg:flex-row lg:gap-6">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <IncludedFilesList
                  files={includedFiles}
                  onPreviewJob={openPreviewJob}
                  includedOrderIds={includedOrderIds}
                  selectedJobIds={selectedJobIds}
                  onThumbnailCheckbox={handleThumbnailCheckbox}
                  onThumbnailDownload={handleThumbnailDownload}
                  canDownloadFile={canDownloadThumbnail}
                  downloadingJobId={downloadingJobId}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <AllFilesList
                files={fileMetadata}
                onPreviewJob={openPreviewJob}
                allFilesOrderIds={allFilesOrderIds}
                selectedJobIds={selectedJobIds}
                onThumbnailCheckbox={handleThumbnailCheckbox}
                onThumbnailDownload={handleThumbnailDownload}
                canDownloadFile={canDownloadThumbnail}
                downloadingJobId={downloadingJobId}
              />
            </div>
          )}

        </div>
      </div>
      </div>

      {previewFile && fileCanPreview(previewFile) ? (
        <BatchMotionJobPreviewModal
          open={Boolean(previewJobId)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setPreviewJobId(null);
            }
          }}
          jobId={previewFile.id}
          jobTitle={previewFile.name}
          batchMotionNumber={batchMotionNumberForPreview}
          totalMotionsInBatch={previewableJobCount}
          exportURLs={previewFile.exportURLs}
          exportFilenames={previewFile.exportFilenames}
          bvhURL={previewFile.bvhURL ?? ''}
          bvhURLs={previewFile.bvhURLs}
          mhrBvhURLs={previewFile.mhrBvhURLs}
          mhrIdentityJsonURLs={previewFile.mhrIdentityJsonURLs}
          cameraFbxURL={previewFile.cameraFbxURL}
          exportSettings={batchExportSettings}
          onDownload={(opts) =>
            store.downloadJob(previewFile.id, previewFile.name, opts)
          }
          pollJobZipExportReadiness={pollJobZipExportReadiness}
          previewNavigation={{
            jobs: previewNavJobs,
            onSelectJobId: openPreviewJob,
          }}
          sourceVideoOverlayUrl={comicSourceOverlay?.url}
          sourceVideoFileExtension={comicSourceOverlay?.fileExtension}
        />
      ) : null}
      {errorModalFile ? (
        <BatchComicJobErrorModal
          open={Boolean(errorModalJobId)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setErrorModalJobId(null);
            }
          }}
          jobId={errorModalFile.id}
          jobTitle={errorModalFile.name}
          batchMotionNumber={batchMotionNumberForErrorModal}
          totalMotionsInBatch={omittedFiles.length}
          errorMessage={errorModalFile.errorMessage ?? ''}
          navigation={{
            jobs: errorNavJobs,
            onSelectJobId: setErrorModalJobId,
          }}
        />
      ) : null}
    </div>
  );
}

function IncludedFilesList({
  files,
  onPreviewJob,
  includedOrderIds,
  selectedJobIds,
  onThumbnailCheckbox,
  onThumbnailDownload,
  canDownloadFile,
  downloadingJobId,
}: {
  files: FileMetadata[];
  onPreviewJob: (jobId: string) => void;
  includedOrderIds: string[];
  selectedJobIds: ReadonlySet<string>;
  onThumbnailCheckbox: (
    orderedIds: string[],
  ) => (jobId: string, event: MouseEvent<HTMLButtonElement>) => void;
  onThumbnailDownload: (
    jobId: string,
    event: MouseEvent<HTMLButtonElement>,
  ) => void | Promise<void>;
  canDownloadFile: (file: BatchMotionThumbnailGridFile) => boolean;
  downloadingJobId: string | null;
}) {
  return (
    <BatchMotionJobThumbnailGrid
      files={files}
      onPreviewJob={onPreviewJob}
      selectionEnabled
      selectedJobIds={selectedJobIds}
      onCheckboxClick={onThumbnailCheckbox(includedOrderIds)}
      onDownloadJob={(jobId, e) => void onThumbnailDownload(jobId, e)}
      canDownloadFile={canDownloadFile}
      downloadingJobId={downloadingJobId}
    />
  );
}

function AllFilesList({
  files,
  onPreviewJob,
  allFilesOrderIds,
  selectedJobIds,
  onThumbnailCheckbox,
  onThumbnailDownload,
  canDownloadFile,
  downloadingJobId,
}: {
  files: FileMetadata[];
  onPreviewJob: (jobId: string) => void;
  allFilesOrderIds: string[];
  selectedJobIds: ReadonlySet<string>;
  onThumbnailCheckbox: (
    orderedIds: string[],
  ) => (jobId: string, event: MouseEvent<HTMLButtonElement>) => void;
  onThumbnailDownload: (
    jobId: string,
    event: MouseEvent<HTMLButtonElement>,
  ) => void | Promise<void>;
  canDownloadFile: (file: BatchMotionThumbnailGridFile) => boolean;
  downloadingJobId: string | null;
}) {
  return (
    <BatchMotionJobThumbnailGrid
      files={files}
      onPreviewJob={onPreviewJob}
      allowErrorExpand
      selectionEnabled
      selectedJobIds={selectedJobIds}
      onCheckboxClick={onThumbnailCheckbox(allFilesOrderIds)}
      onDownloadJob={(jobId, e) => void onThumbnailDownload(jobId, e)}
      canDownloadFile={canDownloadFile}
      downloadingJobId={downloadingJobId}
    />
  );
}

export const ProcessingScreenWithStore = withProvider(ProcessingScreen);
export default ProcessingScreen;
